import { InformationCircleIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import type { CSSProperties } from "react";
import type { JourneyTaskOccurrence } from "../../../domain/track/journeyTaskOccurrenceAdapter";
import { useClientTranslations } from "../../../lib/i18n/client";
import {
	resolveTaskDomain,
	TASK_DOMAIN_COLORS,
} from "../../../lib/journey/taskDomainColors";
import { getEventTypeColor } from "../../../lib/track/eventTypeColor";
import type { Event } from "../../../types/event";
import styles from "./ComingUpCard.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

/**
 * M011 S04 T03 — the Coming Up feed mixes manual events with journey-derived
 * task occurrences. The two shapes are discriminated by `source`: Event has
 * `'patient' | 'clinic' | undefined`, JourneyTaskOccurrence has the literal
 * `'journey'`. The renderer narrows on that field.
 */
export type ComingUpItem = Event | JourneyTaskOccurrence;

function isJourneyOccurrence(
	item: ComingUpItem,
): item is JourneyTaskOccurrence {
	return item.source === "journey";
}

interface ComingUpCardEventProps {
	event: Event;
	onClick: (event: Event) => void;
}

interface ComingUpCardJourneyProps {
	event: JourneyTaskOccurrence;
	onClick: (occurrence: JourneyTaskOccurrence) => void;
}

type ComingUpCardProps = ComingUpCardEventProps | ComingUpCardJourneyProps;

/**
 * ComingUpCard (CUP-02, CUP-03 + M011 S04 T03).
 *
 * A tinted list item for the Coming Up feed. Renders manual events with the
 * type-tinted treatment (test-appt purple, meds orange, cycle pink, admin
 * yellow) or journey-derived task occurrences with a per-taskDomain color
 * (resolved via resolveTaskDomain/TASK_DOMAIN_COLORS) + map-pin leading icon.
 *
 * The leading prop is named `event` for backwards compatibility with the
 * Phase 36/37 Track call sites; the value may be either an Event or a
 * JourneyTaskOccurrence and is narrowed on `source`. The onClick handler is
 * paired to the leading prop's shape — Event callers continue to receive an
 * Event, journey callers receive a JourneyTaskOccurrence.
 */
export function ComingUpCard(props: ComingUpCardProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const item = props.event;

	if (isJourneyOccurrence(item)) {
		const onClick = (props as ComingUpCardJourneyProps).onClick;
		const typeLabel = t("journeyOccurrence.typeLabel");
		const title = item.title?.trim() || t("journeyOccurrence.fallbackTitle");
		const startObj = parseISO(item.startIso);
		const dateText = format(startObj, "EEE, MMM d", { locale: dateFnsLocale });
		const ariaLabel = t("journeyOccurrence.cardAriaLabel", {
			title,
			date: dateText,
		});
		// M011 S07 T11 — resolve per-domain color (drops teal; mirrors TaskCard pattern).
		const domain = resolveTaskDomain(item.taskDomain);
		const domainColor = TASK_DOMAIN_COLORS[domain];
		return (
			<button
				type="button"
				className={`${styles.card} ${styles["card--journey"]}`}
				aria-label={ariaLabel}
				style={{ "--task-domain-color": domainColor } as CSSProperties}
				onClick={() => onClick(item)}
			>
				<div
					className={`${styles.iconCircle} ${styles["iconCircle--journey"]}`}
				>
					<MapPinIcon className={styles.icon} />
				</div>
				<div className={styles.body}>
					<span className={styles.typeLabel}>{typeLabel}</span>
					<h4 className={styles.title}>{title}</h4>
					<p className={styles.dateTime}>@ {dateText}</p>
				</div>
			</button>
		);
	}

	const onClick = (props as ComingUpCardEventProps).onClick;
	const visualCategory = getEventTypeColor(item.type);
	const typeLabel = t(`eventType.${item.type}`);

	// Pitfall 2: parse for time display, slice for day grouping. Time uses device locale.
	const dateObj = parseISO(item.dateTime);
	const dateTimeFormatted = t("event.dateTime", {
		date: format(dateObj, "EEE, MMM d", { locale: dateFnsLocale }),
		time: format(dateObj, "p", { locale: dateFnsLocale }),
	});

	return (
		<button
			type="button"
			className={`${styles.card} ${styles[`card--${visualCategory}`]}`}
			onClick={() => onClick(item)}
		>
			<div
				className={`${styles.iconCircle} ${styles[`iconCircle--${visualCategory}`]}`}
			>
				<InformationCircleIcon className={styles.icon} />
			</div>
			<div className={styles.body}>
				<span className={styles.typeLabel}>{typeLabel}</span>
				<h4 className={styles.title}>{item.title}</h4>
				<p className={styles.dateTime}>@ {dateTimeFormatted}</p>
			</div>
		</button>
	);
}
